import { buildI18n, mandateLineRuleProblem } from "@repo/shared";
import type { LocalizedString, Translated } from "@repo/shared/types";
import {
  type MandateTemplate,
  type MandateTemplateLineInput,
  type MandateTemplateMode,
  type PortfolioCollectionPlatform,
  type ProductListItem,
} from "@repo/shared/types";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { LocalizedInput } from "@/components/ui/localized-input";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateMandateTemplate,
  useUpdateMandateTemplate,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./mandate-template-dialog.i18n";
import {
  emptyLineDraft,
  MandateTemplateLinesEditor,
  type TemplateLineDraft,
} from "./mandate-template-lines-editor";

const parse = (value: string): number | null => {
  if (value.trim() === "") return null;
  const n = Number.parseFloat(value.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

/**
 * The dialog's own pass over the invariants, so Save is disabled with a reason
 * before the server says the same thing. The server validates again.
 */
function clientError(
  t: Translated<typeof T>,
  mode: MandateTemplateMode,
  lines: TemplateLineDraft[],
  uncovered: boolean,
): string | null {
  if (lines.length === 0) return t.needLines;
  if (uncovered) return t.uncovered;
  // The same figure rules, with the same tolerance, the API refuses on.
  const problem = mandateLineRuleProblem(
    mode,
    lines.map((line) =>
      mode === "fixed"
        ? { weightPct: parse(line.weightPct) }
        : {
            minPct: parse(line.minPct),
            defaultPct: parse(line.defaultPct),
            maxPct: parse(line.maxPct),
          },
    ),
  );
  if (!problem) return null;
  switch (problem.code) {
    case "range_order":
      return t.rangeOrder;
    case "fixed_total":
      return t.fixedTotal;
    case "default_total":
      return t.defaultTotal;
    case "range_unreachable":
      return t.rangeUnreachable;
    default:
      return t.pctRange;
  }
}

type MandateTemplateDialogProps = {
  portfolioCollectionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Null = create. */
  template: MandateTemplate | null;
  riskScores: number[];
  platforms: PortfolioCollectionPlatform[];
  products: ProductListItem[];
};

/**
 * QL-375 — create / edit a mandate template. Saves WHOLE: name, risk score,
 * mode, platforms and lines in one request, because the invariants only hold
 * on the full line set.
 */
export function MandateTemplateDialog({
  portfolioCollectionId,
  open,
  onOpenChange,
  template,
  riskScores,
  platforms,
  products,
}: MandateTemplateDialogProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const createTemplate = useCreateMandateTemplate();
  const updateTemplate = useUpdateMandateTemplate();
  const isPending = createTemplate.isPending || updateTemplate.isPending;

  const [name, setName] = useState("");
  // Every translation of the name, edited through the LocalizedInput's
  // language sheet; `name` is the current UI language's value.
  const [nameI18n, setNameI18n] = useState<LocalizedString>({});
  const [riskScore, setRiskScore] = useState<string>("");
  const [mode, setMode] = useState<MandateTemplateMode>("min_max");
  const [platformIds, setPlatformIds] = useState<string[]>([]);
  const [lines, setLines] = useState<TemplateLineDraft[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Seed the form on open — from the template when editing, blank when creating.
  // Depends on the FIRST score (a primitive), not the array: the page rebuilds
  // the array every render, and an array dependency would reseed the form on
  // every parent re-render, wiping what the designer typed.
  const firstScore = riskScores[0];
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (template) {
      setName(localize(template.nameI18n));
      setNameI18n(template.nameI18n);
      setRiskScore(String(template.riskScore));
      setMode(template.mode);
      setPlatformIds(template.platformIds);
      setLines(
        template.lines.map((l) => ({
          productId: l.productId,
          weightPct: l.weightPct == null ? "" : String(l.weightPct),
          minPct: l.minPct == null ? "" : String(l.minPct),
          defaultPct: l.defaultPct == null ? "" : String(l.defaultPct),
          maxPct: l.maxPct == null ? "" : String(l.maxPct),
        })),
      );
    } else {
      setName("");
      setNameI18n({});
      setRiskScore(firstScore != null ? String(firstScore) : "");
      setMode("min_max");
      setPlatformIds([]);
      setLines([]);
    }
  }, [open, template, firstScore, localize]);

  // Switching mode keeps the instruments and clears the figures.
  const changeMode = (next: MandateTemplateMode) => {
    if (next === mode) return;
    setMode(next);
    setLines(lines.map((l) => emptyLineDraft(l.productId)));
  };

  const productById = new Map(products.map((p) => [p.id, p]));
  const uncovered = lines.some((l) => {
    const p = productById.get(l.productId);
    return p ? !platformIds.every((id) => p.platformIds.includes(id)) : false;
  });
  const validation = clientError(t, mode, lines, uncovered);
  const nameMissing = name.trim() === "";

  const handleSubmit = () => {
    if (nameMissing) {
      setError(t.nameRequired);
      return;
    }
    if (validation) {
      setError(validation);
      return;
    }
    setError(null);
    const lineInputs: MandateTemplateLineInput[] = lines.map((l) =>
      mode === "fixed"
        ? { productId: l.productId, weightPct: parse(l.weightPct) }
        : {
            productId: l.productId,
            minPct: parse(l.minPct),
            defaultPct: parse(l.defaultPct),
            maxPct: parse(l.maxPct),
          },
    );
    const data = {
      // The translations typed in the language sheet win; a language left
      // blank falls back to the name in the UI language (create) or keeps the
      // stored value (edit).
      nameI18n: {
        ...(template
          ? template.nameI18n
          : buildI18n(name.trim(), userLanguage, tenantSupportedLanguages)),
        ...Object.fromEntries(
          Object.entries(nameI18n).filter(([, v]) => typeof v === "string" && v.trim() !== ""),
        ),
        [userLanguage]: name.trim(),
      },
      riskScore: Number(riskScore),
      mode,
      platformIds,
      lines: lineInputs,
    };
    const handlers = {
      onSuccess: () => onOpenChange(false),
      onError: (err: Error) => setError(err.message || t.failedToSave),
    };
    if (template) {
      updateTemplate.mutate(
        { collectionId: portfolioCollectionId, templateId: template.id, data },
        handlers,
      );
    } else {
      createTemplate.mutate({ collectionId: portfolioCollectionId, data }, handlers);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{template ? t.editTemplate : t.addTemplate}</DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
            <LocalizedInput
              id="mandate-template-name"
              label={t.name}
              value={name}
              i18nValue={nameI18n}
              onChange={setName}
              onI18nChange={setNameI18n}
              supportedLanguages={tenantSupportedLanguages}
              placeholder={t.namePlaceholder}
              required
              disabled={isPending}
            />
            <div className="space-y-2">
              <Label htmlFor="mandate-template-risk-score">{t.riskScore}</Label>
              <Select value={riskScore} onValueChange={setRiskScore} disabled={isPending}>
                <SelectTrigger id="mandate-template-risk-score">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {riskScores.map((score) => (
                    <SelectItem key={score} value={String(score)}>
                      {score}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {platforms.length > 0 && (
            <div className="space-y-2">
              <Label>{t.platforms}</Label>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {platforms.map((platform) => (
                  <div key={platform.id} className="flex items-center gap-2 text-sm font-normal">
                    <Checkbox
                      id={`mandate-template-platform-${platform.id}`}
                      checked={platformIds.includes(platform.id)}
                      onCheckedChange={() =>
                        setPlatformIds((prev) =>
                          prev.includes(platform.id)
                            ? prev.filter((id) => id !== platform.id)
                            : [...prev, platform.id],
                        )
                      }
                      disabled={isPending}
                    />
                    <Label
                      htmlFor={`mandate-template-platform-${platform.id}`}
                      className="font-normal cursor-pointer"
                    >
                      {localize(platform.labelI18n)}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{t.platformsHelp}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label>{t.mode}</Label>
            <RadioGroup
              value={mode}
              onValueChange={(v) => changeMode(v as MandateTemplateMode)}
              disabled={isPending}
              className="grid gap-2 sm:grid-cols-2"
            >
              <OptionRow align="start">
                <RadioGroupItem value="fixed" id="mandate-template-mode-fixed" />
                <Label htmlFor="mandate-template-mode-fixed" className="font-normal">
                  <span className="block font-medium">{t.modeFixed}</span>
                  <span className="block text-xs text-muted-foreground">{t.modeFixedHint}</span>
                </Label>
              </OptionRow>
              <OptionRow align="start">
                <RadioGroupItem value="min_max" id="mandate-template-mode-min-max" />
                <Label htmlFor="mandate-template-mode-min-max" className="font-normal">
                  <span className="block font-medium">{t.modeMinMax}</span>
                  <span className="block text-xs text-muted-foreground">{t.modeMinMaxHint}</span>
                </Label>
              </OptionRow>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label>{t.instruments}</Label>
            <MandateTemplateLinesEditor
              mode={mode}
              lines={lines}
              onChange={setLines}
              products={products}
              selectedPlatformIds={platformIds}
              disabled={isPending}
            />
          </div>

          {(error ?? (lines.length > 0 ? validation : null)) && (
            <p role="alert" className="text-sm text-destructive">
              {error ?? validation}
            </p>
          )}
        </div>

        <DialogFooter>
          <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
          <Button
            onClick={handleSubmit}
            loading={isPending}
            disabled={nameMissing || validation != null || riskScore === ""}
          >
            {template ? t.save : t.addTemplate}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
